import React, { useState, useRef } from 'react'
import { Loader2, CheckCircle2, XCircle, Clock } from 'lucide-react'

interface McqActivityProps {
  activityData: Record<string, any>
  description?: string
  disabled?: boolean
  onSubmitResponse: (data: Record<string, any>) => Promise<{ success: boolean; error?: string }>
  /** Present only in self-paced mode — called when the player dismisses the explanation */
  onComplete?: () => void | Promise<void>
  /** True when the activity has ended and the correct answer should be revealed (live mode) */
  revealAnswer?: boolean
}

export function McqActivity({ activityData, description, disabled = false, onSubmitResponse, onComplete, revealAnswer = false }: McqActivityProps) {
  const data = activityData || {}
  const options: string[] = data.options || []

  // Support both legacy single-answer and new multi-answer format
  const correctIndices: number[] = Array.isArray(data.correct_answers)
    ? data.correct_answers
    : (typeof data.correct_answer === 'number' ? [data.correct_answer] : [])
  // Respect the explicit multi-select flag set in the lesson builder.
  // Legacy questions (no flag) fall back to "multi only if more than one correct answer".
  const isMultiAnswer = typeof data.multi_select === 'boolean'
    ? data.multi_select
    : correctIndices.length > 1
  const explanation: string | undefined = data.explanation

  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(new Set())
  const [confirmed, setConfirmed] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const hasSubmittedLocally = useRef(false)

  const isLiveMode = !onComplete

  const isCorrect = confirmed && correctIndices.length > 0 && (
    selectedIndices.size === correctIndices.length &&
    correctIndices.every(i => selectedIndices.has(i))
  )

  const handleChoice = (index: number) => {
    if (confirmed) return
    if (isMultiAnswer) {
      setSelectedIndices(prev => {
        const next = new Set(prev)
        next.has(index) ? next.delete(index) : next.add(index)
        return next
      })
    } else {
      setSelectedIndices(new Set([index]))
    }
  }

  const handleConfirm = async () => {
    if (selectedIndices.size === 0 || confirmed) return
    setConfirmed(true)
    hasSubmittedLocally.current = true

    setSubmitting(true)
    try {
      const selectedArr = [...selectedIndices].sort((a, b) => a - b)
      await onSubmitResponse({
        answer: selectedArr[0],
        selected_answers: selectedArr,
        selected_option: selectedArr[0],
        option_text: options[selectedArr[0]],
      })
    } catch {
      // submission failed silently
    } finally {
      setSubmitting(false)
    }
  }

  const handleDismiss = () => {
    if (onComplete) {
      onComplete()
    } else {
      setSubmitted(true)
    }
  }

  // ── Live mode: player just submitted — hide answer until activity ends ──
  if (confirmed && isLiveMode && !revealAnswer) {
    return (
      <div className="p-4 text-center py-12">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[var(--color-primary)]/10 border border-[var(--color-primary)]/30 flex items-center justify-center">
          <Clock className="w-8 h-8 text-[var(--color-primary)]" />
        </div>
        <h3 className="text-lg font-semibold text-theme-text mb-2">Answer Submitted!</h3>
        <p className="text-theme-text-muted text-sm">
          Waiting for your captain to reveal the answer…
        </p>
      </div>
    )
  }

  // ── Reveal mode: activity ended, show correct answer + explanation ──
  if (revealAnswer) {
    return (
      <div className="p-4 space-y-4">
        <div className="rounded-xl overflow-hidden">
          <div className="px-4 py-4">
            <p className="text-sm font-semibold text-green-500 leading-relaxed whitespace-pre-wrap">
              {data.question || ''}
            </p>
            {description && (
              <p className="text-xs text-theme-text-muted mt-2">{description}</p>
            )}
          </div>
        </div>

        {data.question_image_url && (
          <img src={data.question_image_url} alt="" className="max-w-full max-h-64 rounded-lg mx-auto object-contain" />
        )}

        <div className="space-y-2">
          {options.map((option, index) => {
            const isAnswer = correctIndices.includes(index)
            const btnClass = isAnswer
              ? 'border-[var(--color-success)] bg-[var(--color-success-subtle)] text-[var(--color-success)]'
              : 'border-[var(--color-border)] bg-[var(--color-surface)] text-theme-muted opacity-60'

            return (
              <div
                key={index}
                className={`w-full px-4 py-3 rounded-xl border text-sm font-medium flex items-center gap-2 ${btnClass}`}
              >
                <span className="font-mono text-xs">{String.fromCharCode(65 + index)}.</span>
                <span className="flex-1">{option}</span>
                {isAnswer && <CheckCircle2 className="w-4 h-4 shrink-0" />}
              </div>
            )
          })}
        </div>

        {(explanation || correctIndices.length > 0) && (
          <div className="rounded-xl overflow-hidden border border-theme-border">
            {correctIndices.length > 0 && (
              <div className="flex items-center gap-2 px-4 py-2 bg-[var(--color-success-subtle)] border-b border-[var(--color-success)]/30">
                <CheckCircle2 className="w-4 h-4 text-[var(--color-success)] shrink-0" />
                <span className="text-sm font-semibold text-[var(--color-success)]">
                  Correct answer{correctIndices.length > 1 ? 's' : ''}: {correctIndices.map(i => String.fromCharCode(65 + i)).join(', ')}
                </span>
              </div>
            )}
            {explanation && (
              <div className="bg-surface px-4 py-3">
                <p className="text-xs text-theme-text leading-relaxed">{explanation}</p>
              </div>
            )}
            {data.explanation_image_url && (
              <div className="px-4 py-3 border-t border-theme-border">
                <img src={data.explanation_image_url} alt="" className="max-w-full max-h-64 rounded-lg object-contain" />
              </div>
            )}
          </div>
        )}
      </div>
    )
  }

  // ── Already responded in a previous session / dismissed ──
  if (submitted || (disabled && !hasSubmittedLocally.current)) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary-subtle border border-theme-primary flex items-center justify-center">
          <svg className="w-8 h-8 text-theme-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-theme-primary mb-2">Waiting for Next Activity</h3>
        <p className="text-theme-text text-sm">Your captain will start the next activity soon.</p>
      </div>
    )
  }

  // ── Interactive MCQ ──
  return (
    <div className="p-4 space-y-4">
      {/* Question card */}
      <div className="rounded-xl overflow-hidden">
        <div className="px-4 py-4">
          <p className="text-sm font-semibold text-green-500 leading-relaxed whitespace-pre-wrap">
            {data.question || ''}
          </p>
          {description && (
            <p className="text-xs text-theme-text-muted mt-2">{description}</p>
          )}
          {isMultiAnswer && (
            <p className="text-xs text-[var(--color-text-muted)] mt-1 italic">Select all correct answers</p>
          )}
        </div>
      </div>

      {data.question_image_url && (
        <img src={data.question_image_url} alt="" className="max-w-full max-h-64 rounded-lg mx-auto object-contain" />
      )}

      {/* MCQ options */}
      <div className="space-y-2">
        {options.map((option, index) => {
          const isSelected = selectedIndices.has(index)
          const isAnswer = correctIndices.includes(index)

          let btnClass = 'border-[var(--color-border)] bg-[var(--color-surface)] text-theme-text hover:border-[var(--color-primary)] hover:bg-[var(--color-primary)]/10'
          if (confirmed) {
            if (correctIndices.length > 0 && isAnswer) {
              btnClass = 'border-[var(--color-success)] bg-[var(--color-success-subtle)] text-[var(--color-success)]'
            } else if (correctIndices.length > 0 && isSelected && !isAnswer) {
              btnClass = 'border-[var(--color-danger)] bg-[var(--color-danger-subtle)] text-[var(--color-danger)]'
            } else if (correctIndices.length > 0) {
              btnClass = 'border-[var(--color-border)] bg-[var(--color-surface)] text-theme-muted'
            } else if (isSelected) {
              btnClass = 'border-[var(--color-primary)] bg-[var(--color-primary)]/20 text-theme-text'
            } else {
              btnClass = 'border-[var(--color-border)] bg-[var(--color-surface)] text-theme-muted'
            }
          } else if (isSelected) {
            btnClass = 'border-[var(--color-primary)] bg-[var(--color-primary)]/20 text-theme-text'
          }

          return (
            <button
              key={index}
              onClick={() => handleChoice(index)}
              disabled={confirmed}
              className={`w-full text-left px-4 py-3 rounded-xl border text-sm font-medium transition-all flex items-center gap-2 ${btnClass} disabled:cursor-default`}
            >
              {isMultiAnswer ? (
                <span className={`w-4 h-4 shrink-0 rounded border-2 flex items-center justify-center ${isSelected ? 'border-[var(--color-primary)] bg-[var(--color-primary)]' : 'border-current'}`}>
                  {isSelected && <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 10 10"><path d="M1.5 5l2.5 2.5 4.5-4.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                </span>
              ) : null}
              <span className="mr-1 text-theme-muted font-mono text-xs">{String.fromCharCode(65 + index)}.</span>
              {option}
            </button>
          )
        })}
      </div>

      {/* Submit button (before confirm) */}
      {selectedIndices.size > 0 && !confirmed && (
        <button
          onClick={handleConfirm}
          disabled={submitting}
          className="w-full py-3 rounded-lg font-semibold text-sm transition-all hover:opacity-90 disabled:opacity-50 text-[var(--color-on-primary)]"
          style={{ background: 'var(--gradient-primary)' }}
        >
          {submitting ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Submitting…
            </span>
          ) : 'Submit Answer'}
        </button>
      )}

      {/* Feedback panel — self-paced mode only (onComplete present) */}
      {confirmed && !isLiveMode && (
        <div className="rounded-xl overflow-hidden border border-theme-border">
          {correctIndices.length > 0 && (
            <div className={`flex items-center gap-3 px-4 py-3 ${isCorrect ? 'bg-[var(--color-success-subtle)] border-b border-[var(--color-success)]/30' : 'bg-[var(--color-danger-subtle)] border-b border-[var(--color-danger)]/30'}`}>
              {isCorrect
                ? <CheckCircle2 className="w-5 h-5 text-[var(--color-success)] shrink-0" />
                : <XCircle className="w-5 h-5 text-[var(--color-danger)] shrink-0" />
              }
              <span className={`flex-1 min-w-0 break-words font-semibold text-sm ${isCorrect ? 'text-[var(--color-success)]' : 'text-[var(--color-danger)]'}`}>
                {isCorrect
                  ? 'Correct!'
                  : `Incorrect — correct answer${correctIndices.length > 1 ? 's' : ''}: ${correctIndices.map(i => options[i]).filter(Boolean).join(', ')}`
                }
              </span>
            </div>
          )}

          <div className="bg-surface px-4 py-3">
            <p className="text-xs text-theme-text leading-relaxed">
              {explanation || 'No explanation provided.'}
            </p>
          </div>

          {data.explanation_image_url && (
            <div className="px-4 py-3 border-t border-theme-border">
              <img src={data.explanation_image_url} alt="" className="max-w-full max-h-64 rounded-lg object-contain" />
            </div>
          )}

          <div className="px-4 py-3 bg-surface border-t border-theme-border">
            <button
              onClick={handleDismiss}
              disabled={submitting}
              className="w-full py-3 rounded-lg font-semibold text-sm transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed text-[var(--color-on-primary)]"
              style={{ background: 'var(--gradient-primary)' }}
            >
              Start next activity →
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
